/**
 * StepPayment.tsx — Collect card details and start the free trial.
 *
 * Shows plan summary and trial period. On submit, calls the billing checkout
 * endpoint. In dev mode the backend returns a localhost URL — we detect this
 * and advance to the next step without redirecting.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import axios from 'axios';
import API_BASE from '../../../config/api';

interface Props {
  sessionId: string;
  customerId: string;
  accessToken: string;
  plan: string;
  planName: string;
  priceMonthly: number;
  trialDays: number;
  onNext: () => void;
}

export default function StepPayment({
  sessionId, customerId, accessToken, plan, planName, priceMonthly, trialDays, onNext,
}: Props) {
  const navigate = useNavigate();
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry]         = useState('');
  const [cvc, setCvc]               = useState('');
  const [nameOnCard, setNameOnCard] = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');

  function formatCardNumber(v: string) {
    const digits = v.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  }

  function formatExpiry(v: string) {
    const digits = v.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!cardNumber.replace(/\s/g, '') || cardNumber.replace(/\s/g, '').length < 16) {
      setError('Please enter a valid 16-digit card number.'); return;
    }
    if (!expiry || expiry.length < 5) {
      setError('Please enter a valid expiry date (MM/YY).'); return;
    }
    if (!cvc || cvc.length < 3) {
      setError('Please enter a valid CVC.'); return;
    }
    if (!nameOnCard.trim()) {
      setError('Please enter the name on your card.'); return;
    }
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/billing/${customerId}/checkout`,
        {
          plan,
          success_url: `${window.location.origin}/billing?success=1`,
          cancel_url:  `${window.location.origin}/onboarding/${sessionId}`,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      const url: string = data.checkout_url ?? '';
      if (url && !url.includes('localhost') && !url.includes('127.0.0.1')) {
        window.location.href = url;
      } else {
        onNext();
      }
    } catch {
      setError('Could not process payment — please check your card details and try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box maxWidth={520} mx="auto" sx={{ position: 'relative' }}>
      <IconButton
        onClick={() => navigate(-1)}
        sx={{ position: 'absolute', top: 16, right: 16, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <CloseIcon />
      </IconButton>
      <Typography variant="h5" fontWeight={700} textAlign="center" mb={0.5}>
        Start your free trial
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
        Your card will <strong>not</strong> be charged for {trialDays} days. Cancel anytime before then.
      </Typography>

      {/* Plan summary */}
      <Box
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: '1px solid rgba(0,180,216,0.25)',
          bgcolor: 'rgba(0,180,216,0.05)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={700}>{planName} Plan</Typography>
          <Typography variant="caption" color="text.secondary">
            {trialDays}-day free trial, then ${priceMonthly}/month
          </Typography>
        </Box>
        <Typography variant="h6" fontWeight={800} color="primary.main">
          $0 today
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            label="Card number"
            fullWidth
            required
            value={cardNumber}
            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
            placeholder="1234 5678 9012 3456"
            inputProps={{ inputMode: 'numeric', maxLength: 19 }}
          />

          <Grid container spacing={2}>
            <Grid item xs={7}>
              <TextField
                label="Expiry date"
                fullWidth
                required
                value={expiry}
                onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                placeholder="MM/YY"
                inputProps={{ inputMode: 'numeric', maxLength: 5 }}
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="CVC"
                fullWidth
                required
                value={cvc}
                onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="123"
                inputProps={{ inputMode: 'numeric', maxLength: 4 }}
              />
            </Grid>
          </Grid>

          <TextField
            label="Name on card"
            fullWidth
            required
            value={nameOnCard}
            onChange={(e) => setNameOnCard(e.target.value)}
            placeholder="Jane Smith"
          />
        </Stack>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading}
          sx={{ mt: 3, fontWeight: 700, py: 1.5 }}
        >
          {loading
            ? <CircularProgress size={22} color="inherit" />
            : `Start Free Trial — ${trialDays} Days Free →`}
        </Button>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
          <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled">
            Secured by Stripe · 256-bit TLS encryption · No charge for {trialDays} days
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
